// src/pages/About.jsx
import React from "react";
import { Container, Typography, Grid, Box, Button } from "@mui/material";
import { motion } from "framer-motion";
import { styled } from "@mui/material/styles";
import { Link } from "react-router-dom";
import promoImage from "../assets/images/promo-tokens1.png";
import aiImage from "../assets/images/ai-analytics1.png";

/* =============== Brand Glass: violet + blue =============== */
const GLASS = {
  bg: "linear-gradient(135deg, rgba(12,16,26,.96) 0%, rgba(4,7,16,.98) 100%)",
  surface:
    "linear-gradient(180deg, rgba(14,19,33,.95) 0%, rgba(6,9,18,.97) 70%)",
  border: "1px solid rgba(255,255,255,.06)",
  borderHover: "1px solid rgba(255,255,255,.18)",
  shadow: "0 18px 46px rgba(2,8,23,.65)",
  text: "#e5e7eb",
  subtext: "rgba(156,163,175,.96)",
  accentFrom: "#a855f7", // violet
  accentMid: "#6366f1",
  accentTo: "#0ea5e9",   // blue
};

/* =============== Styled =============== */

const PageWrap = styled(Box)({
  minHeight: "100vh",
  paddingTop: 48,
  paddingBottom: 72,
  background:
    "radial-gradient(900px 500px at 0% 0%, rgba(168,85,247,.22), transparent 60%), radial-gradient(900px 500px at 100% 0%, rgba(14,165,233,.18), transparent 55%)",
});

const SectionBox = styled(Box)({
  background: GLASS.bg,
  borderRadius: 20,
  padding: 40,
  boxShadow: GLASS.shadow,
  border: GLASS.border,
  color: GLASS.text,
  backdropFilter: "blur(10px) saturate(120%)",
  WebkitBackdropFilter: "blur(10px) saturate(120%)",
  transition: "transform .28s ease, box-shadow .28s ease, border-color .28s",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 22px 56px rgba(2,8,23,.7)",
    border: GLASS.borderHover,
  },
  "@media (max-width:960px)": { padding: 32 },
  "@media (max-width:600px)": { padding: 24 },
});

const TeamCard = styled(Box)({
  background: GLASS.surface,
  borderRadius: 16,
  padding: 24,
  boxShadow: GLASS.shadow,
  border: GLASS.border,
  color: GLASS.text,
  display: "flex",
  flexDirection: "column",
  transition: "transform .25s ease, box-shadow .25s ease, border-color .25s",
  "&:hover": {
    transform: "translateY(-6px)",
    boxShadow: "0 20px 50px rgba(2,8,23,.7)",
    border: GLASS.borderHover,
  },
});

const AnimatedButton = styled(Button)({
  padding: "14px 28px",
  fontSize: "1.1rem",
  fontFamily: "'Bruno Ace SC', sans-serif",
  background: `linear-gradient(90deg, ${GLASS.accentFrom} 0%, ${GLASS.accentTo} 100%)`,
  borderRadius: 999,
  textTransform: "uppercase",
  color: "#020617",
  boxShadow: "0 16px 40px rgba(15,23,42,.9)",
  "&:hover": {
    boxShadow: "0 22px 56px rgba(15,23,42,1)",
    transform: "translateY(-2px)",
  },
});

const GhostButton = styled(Button)({
  padding: "12px 22px",
  borderRadius: 999,
  fontFamily: "'Bruno Ace SC', sans-serif",
  textTransform: "none",
  color: GLASS.text,
  border: GLASS.border,
  background:
    "linear-gradient(180deg, rgba(16,20,27,0.72) 0%, rgba(6,9,14,0.72) 100%)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,.06)",
  "&:hover": { border: GLASS.borderHover },
});

const FeatureImage = styled("img")({
  width: "100%",
  maxWidth: 560,
  height: "auto",
  borderRadius: 14,
  objectFit: "cover",
  display: "block",
  border: GLASS.border,
  boxShadow: "0 24px 52px rgba(2,8,23,.7)",
});

/* =============== Motion =============== */
const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: "easeOut" },
  },
};

/* =============== Data =============== */
const teamFeatured = [
  {
    name: "Ilya Mitler",
    role: "CEO",
    description: "Leads strategic vision and partnerships.",
  },
  {
    name: "Chan Soj",
    role: "CTO",
    description: "Oversees technical development and innovation.",
  },
  {
    name: "MAX Bitan",
    role: "Lead Developer",
    description: "Drives core platform development.",
  },
];

const teamOthers = [
  {
    name: "",
    role: "Tech Lead / Solidity Architect",
    description:
      "Tech vision, SCAI architecture review, smart contract design and audit (BSC), CI/CD on-chain",
  },
  {
    name: "",
    role: "ML Engineer",
    description:
      "Development and inference of antifraud & asset quality models, feature engineering",
  },
  {
    name: "",
    role: "Data Engineer",
    description:
      "Collecting on-chain and off-chain data (BSC RPC + market feeds), streaming, storage",
  },
  {
    name: "",
    role: "Backend Developer",
    description:
      "REST/GraphQL API, reporting service, authentication, inference orchestration",
  },
  {
    name: "",
    role: "Frontend Developer",
    description:
      "User dashboard, metrics visualization (charts, dashboards), web3 wallet connect",
  },
  {
    name: "",
    role: "DevOps / MLOps",
    description:
      "IaC (Terraform), k8s/ECS, GitHub Actions, Prometheus + Grafana monitoring",
  },
  {
    name: "",
    role: "Product Manager",
    description:
      "Feature prioritization, GTM strategy, beta-client engagement, roadmap planning",
  },
  {
    name: "",
    role: "Security & Audit Advisor",
    description:
      "Smart contract and API pentesting, bug-bounty setup, AML/KYC compliance",
  },
];

/* =============== Component =============== */

function About() {
  return (
    <PageWrap>
      <Container maxWidth="xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: "bold",
              textAlign: "center",
              fontFamily: "'Bruno Ace SC', sans-serif",
              color: GLASS.text,
              mb: 2,
              letterSpacing: ".3px",
              fontSize: { xs: "2rem", md: "2.5rem" },
              background: `linear-gradient(90deg, ${GLASS.accentFrom}, ${GLASS.accentMid}, ${GLASS.accentTo})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            About PromosPay.ai
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              maxWidth: 980,
              mx: "auto",
              color: GLASS.subtext,
              fontSize: "1.12rem",
              mb: { xs: 5, md: 7 },
            }}
          >
            PromosPay.ai is an AI-powered platform with live market streams, on-chain prediction markets . Watch AI hosts, create and trade predictions.
          </Typography>
        </motion.div>

        {/* Why PromosPay.ai? */}
        <SectionBox sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              fontFamily: "'Bruno Ace SC', sans-serif",
              color: GLASS.text,
              mb: 4,
              fontSize: { xs: "1.7rem", md: "2.2rem" },
            }}
          >
            Why PromosPay.ai?
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              alignItems: "center",
              gap: { xs: 3, md: 6 },
            }}
          >
            {/* LEFT: Image */}
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              <FeatureImage src={promoImage} alt="Promo Features" />
            </Box>

            {/* RIGHT: Text */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  background: `linear-gradient(90deg, ${GLASS.accentFrom}, ${GLASS.accentTo})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Trade Predictions
              </Typography>
              <Typography variant="body1" sx={{ color: GLASS.subtext }}>
                Create predictions, bet on your predictions to build trust and attract participants, and trade on the general prediction market.
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  mt: 1,
                  background: `linear-gradient(90deg, ${GLASS.accentFrom}, ${GLASS.accentTo})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                AI hosts & presenters
              </Typography>
              <Typography variant="body1" sx={{ color: GLASS.subtext }}>
                AI hosts that stream the markets and explain the trends in real time.
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  mt: 1,
                  background: `linear-gradient(90deg, ${GLASS.accentFrom}, ${GLASS.accentTo})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Gamification & Monetization
              </Typography>
              <Typography variant="body1" sx={{ color: GLASS.subtext }}>
                Earn through tips, boost tokens, referral programs, and YES/NO
                token trade. Participate in sponsored promotions and claim
                airdrops for top predictions.
              </Typography>

              <Box
                sx={{ mt: 1, display: "flex", gap: 1.5, flexWrap: "wrap" }}
              >
                <AnimatedButton component={Link} to="/features">
                  Learn More
                </AnimatedButton>
                <GhostButton
                  component="a"
                  href="https://app.promospay.ai"
                  target="_blank"
                  rel="noreferrer"
                >
                  Open app
                </GhostButton>
              </Box>
            </Box>
          </Box>
        </SectionBox>

        {/* PromosPay Pro */}
        <SectionBox sx={{ mb: 8 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              fontFamily: "'Bruno Ace SC', sans-serif",
              color: GLASS.text,
              mb: 4,
              fontSize: { xs: "1.7rem", md: "2.2rem" },
            }}
          >
            PromosPay Pro
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              alignItems: "center",
              gap: { xs: 3, md: 6 },
            }}
          >
            {/* IMAGE right on desktop */}
            <Box
              sx={{
                order: { xs: 2, md: 1 },
                display: "flex",
                justifyContent: { xs: "center", md: "flex-start" },
              }}
            >
              <FeatureImage src={aiImage} alt="PromosPay Pro" />
            </Box>

            <Box
              sx={{
                order: { xs: 1, md: 2 },
                display: "flex",
                flexDirection: "column",
                gap: 2.2,
              }}
            >
              <Typography variant="body1" sx={{ color: GLASS.subtext }}>
                The PromosPay Pro subscription unlocks:
              </Typography>
              <Box
                component="ul"
                sx={{ pl: 2.5, color: GLASS.subtext, m: 0 }}
              >
                <li>Priority placement of forecasts in the feed</li>
                <li>
                  Advanced analytics, indicators and metrics of the market and
                  tokens based on artificial intelligence
                </li>
                <li>
                  Exclusive prediction templates and gamified features
                </li>
                <li>Access to promotions from crypto companies</li>
              </Box>
              <Box
                sx={{ mt: 1, display: "flex", gap: 1.5, flexWrap: "wrap" }}
              >
                <AnimatedButton disabled>
                  Try PromosPay Pro (Coming Soon)
                </AnimatedButton>
              </Box>
            </Box>
          </Box>
        </SectionBox>

        {/* Our Team */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: "bold",
            fontFamily: "'Bruno Ace SC', sans-serif",
            color: GLASS.text,
            textAlign: "center",
            mb: 3,
            fontSize: { xs: "1.7rem", md: "2.2rem" },
          }}
        >
          Our Team
        </Typography>

        {/* Top 3 */}
        <Grid container spacing={4} sx={{ mb: 1 }}>
          {teamFeatured.map((member, index) => (
            <Grid
              key={`f-${index}`}
              item
              xs={12}
              sm={6}
              md={4}
              sx={{ display: "flex" }}
            >
              <motion.div
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                transition={{ delay: index * 0.12 }}
                style={{ width: "100%" }}
              >
                <TeamCard sx={{ height: "100%" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      color: GLASS.text,
                      mb: 0.5,
                      fontFamily: "'Bruno Ace SC', sans-serif",
                    }}
                  >
                    {member.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "bold",
                      mb: 1,
                      fontFamily: "'Bruno Ace SC', sans-serif",
                      background: `linear-gradient(90deg, ${GLASS.accentFrom}, ${GLASS.accentTo})`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {member.role}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: GLASS.subtext, fontSize: "0.95rem" }}
                  >
                    {member.description}
                  </Typography>
                </TeamCard>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Others */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {teamOthers.map((member, index) => (
            <Grid
              key={`o-${index}`}
              item
              xs={12}
              sm={6}
              md={4}
              sx={{ display: "flex" }}
            >
              <motion.div
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                transition={{ delay: 0.2 + index * 0.08 }}
                style={{ width: "100%" }}
              >
                <TeamCard sx={{ height: "100%" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      color: GLASS.text,
                      mb: 0.5,
                      fontFamily: "'Bruno Ace SC', sans-serif",
                    }}
                  >
                    {member.name || "—"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "bold",
                      mb: 1,
                      fontFamily: "'Bruno Ace SC', sans-serif",
                      background: `linear-gradient(90deg, ${GLASS.accentFrom}, ${GLASS.accentTo})`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {member.role}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: GLASS.subtext, fontSize: "0.95rem" }}
                  >
                    {member.description}
                  </Typography>
                </TeamCard>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Technical Foundation */}
        <SectionBox>
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              fontFamily: "'Bruno Ace SC', sans-serif",
              color: GLASS.text,
              mb: 3,
              fontSize: { xs: "1.7rem", md: "2.2rem" },
            }}
          >
            Technical Foundation
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: GLASS.subtext, mb: 3 }}
          >
            PromosPay.ai is built on the Binance Smart Chain (BSC) using
            ERC-1155 smart contracts for transparent reward distribution and
            prediction management. We integrate Chainlink oracles for data
            verification, while our AI (SCAI) powers precise analytics and
            content moderation.
          </Typography>
          <AnimatedButton
            component="a"
            href="https://docs.promospay.ai"
            target="_blank"
            rel="noreferrer"
          >
            Read Documentation
          </AnimatedButton>
        </SectionBox>
      </Container>
    </PageWrap>
  );
}

export default React.memo(About);
